<script setup>
import ConfigSettingModal from '@/components/clients/ConfigSettingModal.vue';
import useClients from '@/hooks/useClients';
import ClientLayout from '@/layouts/ClientLayout.vue';
import { onMounted, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useUserStore } from '@/stores/user';

const route = useRoute();
const toast = useToast();
const userStore = useUserStore();
const { hasPermissionTo } = userStore;

const isLoading = ref(false);
const editDialog = ref(false);
const selectedSetting = ref({});
const settingDefs = ref([]);
let initialSettings = {};
const changedSettings = ref({});

const { client, fetchClient, fetchSettingDefinitions, updateClientSetting } = useClients();

onMounted(async () => {
  await fetchClient(route.params.id);
  settingDefs.value = await fetchSettingDefinitions(route.params.id);
  storeInitialSettings();
})

const save = async () => {
  isLoading.value = true;
  try {
    changedSettings.value = settingDefs.value.reduce((changes, setting) => {
      const initialValue = initialSettings[setting.key]
      if (initialValue !== setting.value) {
        changes[setting.key] = setting.value
      }
      return changes
    }, {});

    const response = await updateClientSetting(route.params.id, { settings: changedSettings.value});

    if (response) {
      toast.success("Settings saved.");
      storeInitialSettings();
    }
  } finally {
    isLoading.value = false;
  }
}

const storeInitialSettings = () => {
  initialSettings = settingDefs.value.reduce((acc, setting) => {
    acc[setting.key] = setting.value
    return acc
  }, {})
}

const groupedSettings = computed(() => {
  return settingDefs.value.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {});
});

const hasChanges = computed(() => {
  return settingDefs.value.some(setting => {
    const initialValue = initialSettings[setting.key]
    return initialValue !== setting.value
  })
})

const canUpdateSettings = computed(() => {
  return hasPermissionTo('update_settings')
})

const canUpdateSettingsDescriptions = computed(() => {
  return hasPermissionTo('update_settings_descriptions')
})

const closeDialog = async () => {
  editDialog.value = false;
  settingDefs.value = await fetchSettingDefinitions(route.params.id);
  storeInitialSettings();
}

const openEditDialog = (setting) => {
  selectedSetting.value = setting;
  editDialog.value = true;
}
</script>

<template>
  <ClientLayout :client="client">
    <div class="ma-4" v-if="client">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center border-b bg-surface-light px-4 py-4">
          <div class="text-h4 font-weight-bold">System Configuration</div>
          <v-btn color="primary" :loading="isLoading" :disabled="!hasChanges || isLoading || !canUpdateSettings" @click="save">Save Changes</v-btn>
        </v-card-title>

        <v-table>
          <thead>
            <tr>
              <th>Setting</th>
              <th class="text-left" colspan="2">Value<br />
                <span class="text-info">*Default Value</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(settings, categoryName) in groupedSettings" :key="categoryName">
              <tr class="bg-surface-light">
                <th colspan="3" class="text-left font-weight-bold py-2 border-b border-t">
                  {{ categoryName }}
                </th>
              </tr>
              <tr v-for="(setting, index) in settings" :key="setting.id">
                <td class="text-left" :class="{'border-b': index !== settings.length - 1}">
                  <strong> {{ setting.description }}</strong><br />
                </td>
                <td class="text-left" :class="{'border-b': index !== settings.length - 1}">
                  <div style="width: 200px" class="py-1">
                    <div v-if="setting.value == null">
                      {{ setting.default_value}}
                      <span class="text-info font-weight-bold pr-2">*</span>
                      <v-btn
                        size="x-small"
                        color="primary"
                        class="override-btn"
                        variant="outlined"
                        :disabled="!canUpdateSettings"
                        @click="setting.value = setting.default_value">
                        Override
                      </v-btn>
                    </div>
                    <div v-else>
                      <div v-if="setting.value_type == 'boolean'">
                        <v-switch
                          v-model="setting.value"
                          hide-details
                          density="compact"
                          :disabled="!canUpdateSettings"
                          color="primary"></v-switch>
                      </div>
                      <div v-else-if="setting.value_type == 'integer'">
                        <v-text-field
                          v-model="setting.value"
                          type="number"
                          variant="outlined"
                          hide-details
                          style="width: 100px"
                          :disabled="!canUpdateSettings"
                          density="compact"></v-text-field>
                      </div>
                      <div v-else-if="setting.value_type == 'text'">
                        <v-textarea
                          v-model="setting.value"
                          variant="outlined"
                          density="compact"
                          hide-details
                          :disabled="!canUpdateSettings"
                          rows="2">
                        </v-textarea>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="text-left" :class="{'border-b': index !== settings.length - 1}">
                  <v-btn
                    icon="mdi-pencil"
                    variant="text"
                    density="compact"
                    :disabled="!canUpdateSettingsDescriptions"
                    @click="openEditDialog(setting)">
                  </v-btn>
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </v-card>
    </div>
    <ConfigSettingModal v-if="editDialog" @closeDialog="closeDialog" :setting="selectedSetting" />
  </ClientLayout>
</template>

<style scoped>
.text-h4 {
  /*needs to be inside scope different font used in other components */
  font-family: 'Source Sans 3', 'sans-serif' !important;
}

.override-btn {
  display: none;
}

tr:hover .override-btn {
  display: inline-block;
}

</style>